import type {
  GroupMeeting,
  GroupMeetingAgreement,
  GroupMeetingAgreementCollection,
  GroupMeetingAgreementCollectionDto,
  GroupMeetingAgreementDto,
  GroupMeetingAgreementKind,
  GroupMeetingAiSuggestionsData,
  GroupMeetingDto,
  GroupMeetingMentorClass,
  GroupMeetingMentorClassDto,
  GroupMeetingRatingBucket,
  GroupMeetingRatingBucketDto,
  GroupMeetingRatingType,
  GroupMeetingRatingTypeDto,
  GroupMeetingRatingTypeSummary,
  GroupMeetingRatingTypeSummaryDto,
  GroupMeetingStoreAgreementDto,
  GroupMeetingStudent,
  GroupMeetingStudentDto,
} from "meetings/group-meeting/types";

export const groupMeetingAgreementFromDto = (
  dto: GroupMeetingAgreementDto,
): GroupMeetingAgreement => ({
  id: Number(dto.id),
  mentorId: dto.mentor_id !== null ? Number(dto.mentor_id) : null,
  mentorShortage: dto.mentor_shortage ?? null,
  mentorName: dto.mentor_name ?? null,
  mentorClass: dto.mentor_class,
  type: dto.type,
  value: dto.value,
  createdAt: dto.created_at ?? null,
  meta: {
    canUpdate: Boolean(dto.meta.can_update),
    canDelete: Boolean(dto.meta.can_delete),
  },
});

export const groupMeetingAgreementCollectionFromDto = (
  dto: GroupMeetingAgreementCollectionDto,
): GroupMeetingAgreementCollection => ({
  data: dto.data.map(groupMeetingAgreementFromDto),
  meta: { canCreate: Boolean(dto.meta.can_create) },
});

export const groupMeetingAgreementToDto = (input: {
  mentorClass: string;
  kind: GroupMeetingAgreementKind;
  value: string;
  aiSuggestionsData?: GroupMeetingAiSuggestionsData;
}): GroupMeetingStoreAgreementDto => ({
  mentor_class: input.mentorClass,
  type: input.kind,
  value: input.value,
  ai_suggestion_clicked:
    input.aiSuggestionsData?.hasFetchedAISuggestions ?? false,
  ai_suggestion_accepted:
    input.aiSuggestionsData?.aiSuggestionAccepted ?? false,
  ai_suggestion_accepted_quote:
    input.aiSuggestionsData?.aiSuggestionAcceptedQuote ?? "",
});

const groupMeetingRatingBucketFromDto = (
  dto: GroupMeetingRatingBucketDto,
): GroupMeetingRatingBucket => ({
  rating: dto.rating !== null ? Number(dto.rating) : null,
  count: Number(dto.count),
  shortages: dto.shortages,
  remarks: dto.remarks.map((remark) => ({
    teacherShortage: remark.teacher_shortage ?? null,
    remark: remark.remark ?? null,
  })),
});

const groupMeetingRatingTypeSummaryFromDto = (
  dto: GroupMeetingRatingTypeSummaryDto,
): GroupMeetingRatingTypeSummary => ({
  id: Number(dto.id),
  filled:
    dto.filled !== undefined && dto.filled !== null ? Number(dto.filled) : null,
  total:
    dto.total !== undefined && dto.total !== null ? Number(dto.total) : null,
  ratings: dto.ratings.map(groupMeetingRatingBucketFromDto),
});

export const groupMeetingStudentFromDto = (
  dto: GroupMeetingStudentDto,
): GroupMeetingStudent => ({
  id: Number(dto.id),
  name: dto.name,
  profilePicture: dto.profile_picture ?? null,
});

export const groupMeetingRatingTypeFromDto = (
  dto: GroupMeetingRatingTypeDto,
): GroupMeetingRatingType => ({
  id: Number(dto.id),
  name: dto.name,
  description: dto.description ?? null,
});

export const groupMeetingMentorClassFromDto = (
  dto: GroupMeetingMentorClassDto,
): GroupMeetingMentorClass => ({
  title: dto.title,
  // Chats pass through verbatim: the shared legacy c-chat-component consumes
  // the snake_case rows — see GroupMeetingChatDto.
  chats: dto.chats,
  goals: groupMeetingAgreementCollectionFromDto(dto.goals),
  agreements: groupMeetingAgreementCollectionFromDto(dto.agreements),
  mentors: dto.mentors,
  students: dto.students.map(groupMeetingStudentFromDto),
  ratingTypes: dto.rating_types.map(groupMeetingRatingTypeSummaryFromDto),
});

export const groupMeetingFromDto = (dto: GroupMeetingDto): GroupMeeting => ({
  id: Number(dto.id),
  meetingType: "group_meeting",
  name: dto.name,
  message: dto.message ?? null,
  schoolId: Number(dto.school_id),
  schoolYear: dto.school_year ?? null,
  status: dto.status,
  teacherPercentage:
    dto.teacher_percentage !== undefined && dto.teacher_percentage !== null
      ? Number(dto.teacher_percentage)
      : null,
  totalGivenRatings: Number(dto.total_given_ratings),
  totalRatings: Number(dto.total_ratings),
  ratingTypes: dto.rating_types.map(groupMeetingRatingTypeFromDto),
  mentorClasses: dto.mentor_classes.map(groupMeetingMentorClassFromDto),
});
